import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Field,
  Input,
  SegmentedControl,
} from '../../../components/ui';
import { renameOrganization } from '../governmentApi';

type Kind = 'official' | 'correction';

const EXPLAINS: Record<Kind, string> = {
  official:
    'Something happened to the entity — a decree renamed it. It joins the name history, dated and sourced.',
  correction:
    'Nothing happened to the entity; the record was written wrong. Kept in the audit log, not in the name history.',
};

const messageOf = (error: any): string => {
  const detail = error?.response?.data?.message;
  if (Array.isArray(detail)) return detail.join(', ');
  if (detail) return String(detail);
  return error?.message ?? 'The rename could not be saved';
};

/**
 * Renaming a record and renaming an entity are different acts.
 *
 * An official change is part of what happened to the institution and is kept
 * with the date it took effect and the act behind it; a correction only fixes
 * how the record was written.
 */
export const RenameDialog: React.FC<{
  open: boolean;
  organizationId: string;
  currentName: string;
  currentAcronym?: string | null;
  onClose: () => void;
  onDone: (newName: string) => void;
}> = ({ open, organizationId, currentName, currentAcronym, onClose, onDone }) => {
  const [kind, setKind] = useState<Kind>('official');
  const [name, setName] = useState(currentName);
  const [acronym, setAcronym] = useState(currentAcronym ?? '');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [source, setSource] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setKind('official');
    setName(currentName);
    setAcronym(currentAcronym ?? '');
    setEffectiveDate('');
    setSource('');
    setSourceUrl('');
    setError(null);
  }, [open, currentName, currentAcronym]);

  const official = kind === 'official';
  const changed =
    name.trim() !== currentName.trim() || acronym.trim() !== (currentAcronym ?? '').trim();
  const ready = name.trim().length > 0 && changed && (!official || !!effectiveDate);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const { organization } = await renameOrganization(organizationId, {
        name: name.trim(),
        acronym: acronym.trim() || null,
        kind,
        effectiveDate: official ? effectiveDate : null,
        source: official ? source.trim() || null : null,
        sourceUrl: official ? sourceUrl.trim() || null : null,
      });
      onDone(organization.name);
      onClose();
    } catch (failure: any) {
      setError(messageOf(failure));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} label="Rename" handle className="sm:max-w-md">
      <DialogHeader eyebrow={currentName} title="Rename" onClose={onClose} />
      <DialogBody className="space-y-4">
        <div>
          <SegmentedControl<Kind>
            label="What kind of change"
            items={[
              { value: 'official', label: 'Official change' },
              { value: 'correction', label: 'Correction' },
            ]}
            value={kind}
            onChange={setKind}
          />
          <p className="mt-2 text-[12px] leading-snug text-neutral-500">{EXPLAINS[kind]}</p>
        </div>

        <Field label="New name" htmlFor="rename-name" required>
          <Input
            id="rename-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            autoComplete="off"
          />
        </Field>

        <Field label="Acronym" htmlFor="rename-acronym">
          <Input
            id="rename-acronym"
            value={acronym}
            onChange={(event) => setAcronym(event.target.value)}
            autoComplete="off"
          />
        </Field>

        {official && (
          <>
            <Field label="Took effect" htmlFor="rename-date" required>
              <Input
                id="rename-date"
                type="date"
                value={effectiveDate}
                onChange={(event) => setEffectiveDate(event.target.value)}
              />
            </Field>
            <Field label="Act or decree" htmlFor="rename-source">
              <Input
                id="rename-source"
                value={source}
                onChange={(event) => setSource(event.target.value)}
                placeholder="Décret D/2026/042/PRG"
                autoComplete="off"
              />
            </Field>
            <Field label="Link" htmlFor="rename-url">
              <Input
                id="rename-url"
                type="url"
                value={sourceUrl}
                onChange={(event) => setSourceUrl(event.target.value)}
                placeholder="https://"
                autoComplete="off"
              />
            </Field>
          </>
        )}

        {error && (
          <p role="alert" className="rounded-xl bg-primary-50 px-3.5 py-2.5 text-[12.5px] text-primary-700">
            {error}
          </p>
        )}
      </DialogBody>
      <DialogFooter>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" loading={busy} disabled={!ready} onClick={save}>
          Rename
        </Button>
      </DialogFooter>
    </Dialog>
  );
};
